import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { api } from '../api/client.js';
import { startArticleRequest } from '../api/articleRequest.js';
import { useLang, useT } from '../i18n/LangContext.jsx';
import T from '../i18n/content.js';
import ArticleView from './ArticleView.jsx';

// Keyed by id in the route wrapper: never render a previous article's body
// while the next article is waiting for its request effect to run.
export default function ArticleDetail({ id, kind }) {
  const { lang } = useLang();
  const t = useT();
  const navigate = useNavigate();
  const initialLang = useRef(lang);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: 'pending', item: null });
  const load = kind === 'media' ? api.mediaItem : api.insight;
  const copy = T.articleDetail;

  useEffect(() => {
    let active = true;
    const request = startArticleRequest(load, id);
    request.promise.then(item => {
      if (active) setState({ status: item ? 'ready' : 'missing', item });
    }).catch(error => {
      if (!active) return;
      const status = error?.status === 404 ? 'missing'
        : error?.name === 'TimeoutError' ? 'timeout' : 'error';
      setState({ status, item: null });
    });
    return () => { active = false; request.cancel(); };
  }, [id, load, attempt]);

  // A language switch leaves the single-language page even during loading.
  if (lang !== initialLang.current) return <Navigate to="/insights" replace />;
  if (state.status === 'pending') {
    return <div className="article-pending" aria-busy="true" aria-label={t(copy.loading)} />;
  }
  if (state.status === 'ready') {
    const item = state.item;
    if ((item.lang && item.lang !== lang) || (kind === 'media' && item.type !== 'article')) {
      return <Navigate to="/insights" replace />;
    }
    return <ArticleView item={item} backTo="/insights" />;
  }

  const retry = () => {
    setState({ status: 'pending', item: null });
    setAttempt(value => value + 1);
  };
  return (
    <section className="section article-request-error">
      <p role="alert">{t(copy[state.status])}</p>
      {state.status !== 'missing' && <button type="button" className="btn-ghost" onClick={retry}>{t(T.insightsSec.retry)}</button>}
      <button type="button" className="btn-ghost" onClick={() => navigate('/insights')}>{t(copy.back)}</button>
    </section>
  );
}
